import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, tap } from "rxjs";
import { KMR_HTTP_SERVICE, KmrHttpService } from "./kmr-http.service";
import { HttpInterface } from "./http.interface";

export interface KmrAccessModel {
    mapped: boolean;
    kmrUserId?: number;
    roles?: string[];
    isFranchise: boolean;
    isAdmin: boolean;
}

const EMPTY_ACCESS: KmrAccessModel = { mapped: false, isFranchise: false, isAdmin: false };

/**
 * Caches the current POS user's KMR area membership (GET api/kmr/access/me), resolved server-side
 * from KMR's own User/UserRole tables - see KisnaMeriRoshaniAPI's AccessController/AccessControlService.
 * Used only to decide whether to show the KMR menu items; KisnaMeriRoshaniAPI itself remains the
 * real enforcement point on every request, so nothing here needs to be treated as a security
 * boundary - the POS side never invents its own KMR permission records.
 */
@Injectable({ providedIn: 'root' })
export class KmrAccessService {
    private _access$ = new BehaviorSubject<KmrAccessModel>(EMPTY_ACCESS);
    access$ = this._access$.asObservable();

    // Flips true once the first kmr/access/me call (success or failure) has resolved, so callers that
    // need the real area (franchise/admin) before firing their own KMR calls - e.g. KmrPlansComponent -
    // can wait for it instead of reading `current`'s EMPTY_ACCESS default mid-flight and misrouting to
    // the admin API as a plain franchise/store user.
    private _loaded$ = new BehaviorSubject<boolean>(false);
    loaded$ = this._loaded$.asObservable();

    get current(): KmrAccessModel {
        return this._access$.value;
    }

    get isLoaded(): boolean {
        return this._loaded$.value;
    }

    constructor(@Inject(KMR_HTTP_SERVICE) private _kmrHttp: HttpInterface) { }

    refresh() {
        this._kmrHttp.get<{ data: KmrAccessModel }>('kmr/access/me').pipe(
            tap({
                error: () => this._access$.next(EMPTY_ACCESS)
            })
        ).subscribe({
            next: (response) => { this._access$.next(response?.data ?? EMPTY_ACCESS); this._loaded$.next(true); },
            error: () => { this._access$.next(EMPTY_ACCESS); this._loaded$.next(true); }
        });
    }

    reset() {
        this._access$.next(EMPTY_ACCESS);
        this._loaded$.next(false);
    }
}
